import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { Button } from '@gik/ui/Button';
import DotsHorizontalIcon from '@heroicons/react/outline/DotsHorizontalIcon';
import PhotographIcon from '@heroicons/react/outline/PhotographIcon';
import React from 'react';
import type { OnChangeHandlerFunc, SuggestionDataItem } from 'react-mentions';
import { InputMentions } from '../../../ui/Mentions';
import { Popover } from '../../../ui/Popover';
import { PopoverMenu, PopoverMenuItem } from '../../../ui/PopoverMenu';
import { SvgIcon } from '../../../ui/SvgIcon';

export type StreamInputVariant = 'neutral';

export type StreamInputProps = {
  value?: string;
  level?: number;
  hasImageUpload?: boolean;
  hasBgSelector?: boolean;
  hasMore?: boolean;
  preview?: boolean;
  placeholder?: string;
  variant?: StreamInputVariant;
  users?: SuggestionDataItem[];
  onChange?: OnChangeHandlerFunc;
  onImageUpload?: () => void;
  onSelectBg?: () => void;
} & UIComponent;

function StreamInputComp({
  className,
  hasImageUpload,
  hasBgSelector,
  hasMore,
  value,
  level,
  variant,
  preview,
  onChange = noop,
  onImageUpload = noop,
  onSelectBg = noop,
  users,
  ...otherProps
}: StreamInputProps): React.ReactElement {
  const bem = useBemCN('stream-input');

  const handleImageUploadClick = React.useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.preventDefault();
      ev.stopPropagation();
      onImageUpload?.();
    },
    [onImageUpload]
  );

  const handleBgClick = React.useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.preventDefault();
      ev.stopPropagation();
      onSelectBg?.();
    },
    [onSelectBg]
  );

  const imageUploadButton = (
    <Button {...bem('bg-icon', null, ['gik-base-attach-button'])} circle size="sm" onClick={handleImageUploadClick}>
      <SvgIcon Icon={PhotographIcon} />
    </Button>
  );

  const bgSelectorButton = (
    <Button
      circle
      {...bem('btn-toggle', null, ['gik-stream-background-picker-button'])}
      uppercase={false}
      onClick={handleBgClick}
    >
      Aa
    </Button>
  );

  return (
    <div {...bem(null, [{ [`variant-${variant}`]: variant }], className)}>
      <InputMentions value={value} onChange={onChange} users={users} {...otherProps} padding />
      <div {...bem('action-icon-wrapper')}>
        {hasImageUpload && imageUploadButton}
        {hasBgSelector && bgSelectorButton}
      </div>
      {hasMore && (
        <Popover
          noPad
          trigger={
            <div {...bem('action-icon-wrapper')}>
              <div {...bem('action-icon')}>
                <SvgIcon Icon={DotsHorizontalIcon} />
              </div>
            </div>
          }
        >
          <PopoverMenu>
            <PopoverMenuItem>Click Me</PopoverMenuItem>
            <PopoverMenuItem>Do Something</PopoverMenuItem>
          </PopoverMenu>
        </Popover>
      )}
    </div>
  );
}

export const StreamInput = withComponentErrorBoundary(StreamInputComp);
